import styled from '@emotion/styled';

import DrawerCloseIcon from '@/assets/icons/icon_header_nav_close.svg';
import DrawerOpenIcon from '@/assets/icons/icon_header_nav_menu.svg';
import WalletIcon from '@/assets/icons/icon_header_nav_wallet.svg';
import ToggleIcon from '@/assets/icons/icon_nav_open.svg';
import { mq } from '@/utility/mediaQuery';
import { defaultTheme } from '@/utility/theme';
import { disclosure, hoverOpacity } from '@/utility/theme/animation';

export const PC_NAV_HEIGHT = 78;
export const PC_CROSS_BANNER_HEIGHT = 50;
export const SP_NAV_HEIGHT = 64;

const getIconCss = (
  url: string,
  width: string | string[],
  height: string | string[],
) => {
  return {
    display: 'inline-block',
    width,
    height,
    backgroundImage: `url(${url})`,
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
  };
};

export const NavWrapper = styled.nav(() =>
  mq({
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    height: [
      `${SP_NAV_HEIGHT}px`,
      `${SP_NAV_HEIGHT}px`,
      `${SP_NAV_HEIGHT}px`,
      `${PC_NAV_HEIGHT}px`,
    ],
    color: defaultTheme.color.white,
    backgroundColor: defaultTheme.background.secondary,
    padding: `0 ${defaultTheme.spacing.s}`,
  }),
);

export const ListWrapper = styled.ul(() =>
  mq({
    display: 'flex',
    alignItems: 'center',
  }),
);

export const ScrollNavWrapper = styled.div(() =>
  mq({
    width: ['94vw', '94vw', '80%', '50%'],
    // marginRight: 'auto',
    marginLeft: '0px',
    display: 'flex',
    justifyContent: 'left',
  }),
);

export const ScrollWrapper = styled.div(() =>
  mq({
    padding: `${defaultTheme.spacing.m} 0`,
    overflowX: ['auto', 'auto', 'hidden', 'hidden'],
  }),
);

export const ScrollList = styled.ul(() =>
  mq({
    display: 'flex',
    alignItems: 'left',
    justifyContent: 'flex-start',
  }),
);

export const ScrollItem = styled.ul(() =>
  mq({
    paddingTop: [
      defaultTheme.spacing.m,
      defaultTheme.spacing.m,
      defaultTheme.spacing.m,
      defaultTheme.spacing.l,
    ],
    paddingBottom: [
      defaultTheme.spacing.m,
      defaultTheme.spacing.m,
      defaultTheme.spacing.m,
      defaultTheme.spacing.l,
    ],
    paddingLeft: [
      defaultTheme.spacing.s,
      defaultTheme.spacing.s,
      defaultTheme.spacing.l,
      defaultTheme.spacing.l,
    ],
    ...hoverOpacity,
  }),
);

export const ScrollLink = styled.div(() =>
  mq({
    '& > a, & > span': {
      display: 'flex',
      justifyContent: 'left',
      fontSize: [
        defaultTheme.font.size.s,
        defaultTheme.font.size.s,
        defaultTheme.font.size.s,
        defaultTheme.font.size.m,
      ],
      color: defaultTheme.text.white,
      paddingRight: [
        defaultTheme.spacing.m,
        defaultTheme.spacing.m,
        defaultTheme.spacing.xxl,
        defaultTheme.spacing.xxl,
      ],
      wordBreak: 'keep-all',
    },
  }),
);

export const ListItem = styled.li(() =>
  mq({
    fontSize: [
      defaultTheme.font.size.s,
      defaultTheme.font.size.s,
      defaultTheme.font.size.s,
      defaultTheme.font.size.m,
    ],
    marginRight: [0, 0, 0, defaultTheme.spacing.l],
    '& > a': {
      display: 'inline-block',
      padding: [
        defaultTheme.spacing.s,
        defaultTheme.spacing.s,
        defaultTheme.spacing.s,
        defaultTheme.spacing.l,
      ],
      ...hoverOpacity,
    },
  }),
);

export const RightBox = styled.div(() =>
  mq({
    display: 'flex',
    alignItems: 'center',
  }),
);

export const DrawerButton = styled.button(() =>
  mq({
    flex: '1 0 auto',
    display: 'flex',
    alignItems: 'center',
    height: ['32px', '32px', '32px', '46px'],
    padding: [
      `0 ${defaultTheme.spacing.s}`,
      `0 ${defaultTheme.spacing.s}`,
      `0 ${defaultTheme.spacing.s}`,
      `0 ${defaultTheme.spacing.l}`,
    ],
    ...hoverOpacity,
  }),
);

export const DrawerIcon = styled.i((props: { isOpen: boolean }) =>
  mq({
    ...getIconCss(
      props.isOpen ? DrawerCloseIcon : DrawerOpenIcon,
      ['24px', '24px', '24px', '32px'],
      ['18px', '18px', '18px', '24px'],
    ),
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
  }),
);

export const UserIcon = styled.img(() =>
  mq({
    width: ['28px', '28px', '28px', '46px'],
    height: ['28px', '28px', '28px', '46px'],
    objectFit: 'cover',
    margin: [
      `0 ${defaultTheme.spacing.s} 0 0`,
      `0 ${defaultTheme.spacing.s} 0 0`,
      `0 ${defaultTheme.spacing.s} 0 0`,
      `0 ${defaultTheme.spacing.l} 0 32px`,
    ],
    ...hoverOpacity,
  }),
);

export const SpBox = styled.div(() =>
  mq({
    display: ['flex', 'flex', 'flex', 'none'],
    flexDirection: ['column', 'column', 'column', 'row'],
    justifyContent: 'space-between',
    alignItems: ['center', 'center', 'center', 'inherit'],
    width: '100%',
    color: defaultTheme.text.white,
    backgroundColor: defaultTheme.background.secondary,
    border: `solid ${defaultTheme.border.color.darkGray}`,
    borderWidth: '1px 0',
    padding: defaultTheme.spacing.l,
    '&:empty': {
      display: 'none',
    },
  }),
);

export const PcBox = styled.div(() =>
  mq({
    display: ['none', 'none', 'none', 'flex'],
    alignItems: 'center',
  }),
);

export const WalletWrapper = styled.div((props: { isOpen: boolean }) =>
  mq({
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: props.isOpen
      ? [
          defaultTheme.spacing.xl,
          defaultTheme.spacing.xl,
          defaultTheme.spacing.xl,
          0,
        ]
      : 0,
    ...disclosure(props.isOpen, '70px'),
  }),
);

export const BalanceWrapper = styled.button(() =>
  mq({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    width: '100%',
    height: '16px',
    border: 'none',
  }),
);

export const WalletToggleIcon = styled.i((props: { isOpen: boolean }) =>
  mq({
    ...getIconCss(ToggleIcon, '18px', '10px'),
    transform: `rotate(${props.isOpen ? '0deg' : '180deg'})`,
    transition: '.2s all ease-in-out',
  }),
);

export const TotalBalance = styled.p(() =>
  mq({
    position: 'absolute',
    right: '0',
    fontWeight: 'bold',
    lineHeight: defaultTheme.font.height.base,
  }),
);

export const WalletMark = styled.i(() =>
  mq({
    ...getIconCss(WalletIcon, '12px', '12px'),
    marginRight: defaultTheme.spacing.xs,
  }),
);

export const NonMemberWrapper = styled.div(() =>
  mq({
    marginRight: [
      defaultTheme.spacing.s,
      defaultTheme.spacing.s,
      defaultTheme.spacing.s,
      defaultTheme.spacing.l,
    ],
  }),
);

export const NonMemberArea = styled.div(() =>
  mq({
    display: ['flex', 'flex', 'flex', 'none'],
    flexDirection: ['column', 'column', 'column', 'row'],
    justifyContent: 'space-between',
    alignItems: ['center', 'center', 'center', 'inherit'],
    width: '100%',
    color: defaultTheme.text.white,
    backgroundColor: defaultTheme.background.secondary,
    border: `solid ${defaultTheme.border.color.darkGray}`,
    borderWidth: '1px 0',
    padding: defaultTheme.spacing.s,
    '&:empty': {
      display: 'none',
    },
  }),
);
